/**
 * useScanFeedback - Unified scan feedback for station screens
 *
 * Provides:
 * - Error modal state management
 * - Sound triggers (success/error)
 * - Convenience methods for common scenarios
 *
 * IMPORTANT: Sound Coordination with QRScannerInput
 * - QRScannerInput plays success beep on scan capture (immediate feedback)
 * - DO NOT call triggerSuccess() after scan operations (causes double beep)
 * - triggerSuccess() is for non-scan actions (Accept button, complete packing, etc.)
 * - triggerError/Warning are safe to use after scans (different sound, shows modal)
 *
 * Usage:
 * const feedback = useScanFeedback();
 *
 * // For errors (safe after scans - different sound + modal):
 * feedback.triggerError('Scan Error', 'Garment marked for redo');
 *
 * // For non-scan success actions:
 * feedback.triggerSuccess(); // Accept button, complete packing, etc.
 *
 * // For redo garments with replacement info:
 * feedback.triggerRedoBlocked(replacement);
 *
 * // Render modal:
 * <ScanErrorModal {...feedback.modalProps} />
 */
import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { ReplacementGarmentStatus } from '@threadheads/shared';
import { playSuccessBeep, playErrorBeep } from '@/lib/sounds';

interface ReplacementInfo {
  batchName: string | null;
  stage: string;
}

interface ScanFeedbackState {
  showModal: boolean;
  title: string;
  message: string;
  secondaryMessage: string | undefined;
  severity: 'error' | 'warning' | 'success';
}

interface SupersededState {
  open: boolean;
  orderNumber: string | null;
  replacement: ReplacementGarmentStatus | null;
}

export function useScanFeedback() {
  const { t } = useTranslation('production');
  const [state, setState] = useState<ScanFeedbackState>({
    showModal: false,
    title: '',
    message: '',
    secondaryMessage: undefined,
    severity: 'error',
  });

  // SUPERSEDED sticker (2026-07-08): its own dialog (not the ScanErrorModal) so
  // it can carry a one-tap "Print new sticker" action. Amber/warning severity —
  // recoverable, and must read differently from the red redo "discard" modal.
  const [superseded, setSuperseded] = useState<SupersededState>({
    open: false,
    orderNumber: null,
    replacement: null,
  });

  /**
   * Play success beep only (no modal).
   * IMPORTANT: Do NOT call this after scan operations - QRScannerInput handles that.
   * Use for non-scan actions: Accept button, Complete Packing button, etc.
   */
  const triggerSuccess = useCallback(() => {
    playSuccessBeep();
    // No modal for success - just sound
  }, []);

  /**
   * Show error modal with sound.
   * Safe to call after scan operations (error sound is different from success).
   */
  const triggerError = useCallback((title: string, message: string, secondaryMessage?: string) => {
    playErrorBeep();
    setState({
      showModal: true,
      title,
      message,
      secondaryMessage,
      severity: 'error',
    });
  }, []);

  /**
   * Show warning modal with sound.
   * Safe to call after scan operations.
   */
  const triggerWarning = useCallback((title: string, message: string, secondaryMessage?: string) => {
    playErrorBeep(); // Same sound for warnings (attention needed)
    setState({
      showModal: true,
      title,
      message,
      secondaryMessage,
      severity: 'warning',
    });
  }, []);

  /**
   * Terminal SUCCESS acknowledgement — a completed run the operator must dismiss.
   *
   * PRODUCTION_DESIGN §10: "Long-running prints without explicit confirmation.
   * Operator may walk away. Always end a print job with an 'OK, all printed'
   * confirmation." Deliberately does NOT call `playErrorBeep()` — on the floor that
   * beep MEANS something is wrong, so using the warning channel for a clean run would
   * signal failure at the exact moment we are trying to signal success. Callers own
   * the positive chime (they already play it).
   */
  const triggerSuccessModal = useCallback((title: string, message: string, secondaryMessage?: string) => {
    setState({
      showModal: true,
      title,
      message,
      secondaryMessage,
      severity: 'success',
    });
  }, []);

  /**
   * Convenience method for already-scanned scenario.
   * Shows warning modal with appropriate message.
   */
  const triggerAlreadyScanned = useCallback((itemType = 'item') => {
    triggerWarning(t('scanFeedback.alreadyScanned'), t('scanFeedback.alreadyScannedDesc', { itemType }));
  }, [triggerWarning, t]);

  /**
   * Convenience method for redo/failed garment with replacement info.
   * Shows error modal explaining user should discard garment.
   */
  const triggerRedoBlocked = useCallback((replacement?: ReplacementInfo | null) => {
    let secondaryMessage: string | undefined;
    if (replacement) {
      if (replacement.batchName) {
        secondaryMessage = t('scanFeedback.replacementInfo', { batchName: replacement.batchName, stage: replacement.stage });
      } else {
        secondaryMessage = t('scanFeedback.replacementStage', { stage: replacement.stage });
      }
    }
    triggerError(
      t('scanFeedback.redoDiscard'),
      t('scanFeedback.redoDiscardDesc'),
      secondaryMessage
    );
  }, [triggerError, t]);

  /**
   * SUPERSEDED sticker scan (2026-07-08): the scanned sticker was invalidated by
   * a re-batch. The shirt is fine — do NOT discard it. Opens the dedicated
   * SupersededStickerDialog (amber) with a one-tap reprint of the LIVE
   * replacement, or a "give it to your supervisor" message when the order was
   * reset but not yet re-batched (replacement null). Warning buzz — recoverable,
   * distinct from the red redo modal. Covers both cases (has/no replacement).
   */
  const triggerSuperseded = useCallback(
    (replacement: ReplacementGarmentStatus | null, orderNumber?: string | null) => {
      playErrorBeep(); // warning buzz (attention needed; visual twin is the amber dialog)
      setSuperseded({ open: true, orderNumber: orderNumber ?? null, replacement });
    },
    [],
  );

  const dismissSuperseded = useCallback(() => {
    setSuperseded((prev) => ({ ...prev, open: false }));
  }, []);

  /**
   * Convenience method for order cancelled scenario.
   */
  const triggerOrderCancelled = useCallback(() => {
    triggerError(t('scanFeedback.orderCancelled'), t('scanFeedback.orderCancelledDesc'), t('scanFeedback.orderCancelledAction'));
  }, [triggerError, t]);

  /**
   * BLU-285: Convenience method for order item removed by customer edit.
   * Uses warning (amber) severity — the order is not cancelled, just edited.
   */
  const triggerOrderItemRemoved = useCallback(() => {
    triggerWarning(
      t('scanFeedback.orderItemRemoved'),
      t('scanFeedback.orderItemRemovedDesc'),
      t('scanFeedback.orderItemRemovedAction'),
    );
  }, [triggerWarning, t]);

  /**
   * BLU-695: convenience method for a garment cancelled by an order-change
   * drift correction (cancelSurplusUnits) — distinct from
   * triggerOrderItemRemoved's order-sync pre-batch removal.
   */
  const triggerOrderChangedRemoved = useCallback(() => {
    triggerWarning(
      t('scanFeedback.orderChangedRemoved'),
      t('scanFeedback.orderChangedRemovedDesc'),
      t('scanFeedback.orderChangedRemovedAction'),
    );
  }, [triggerWarning, t]);

  /**
   * Part 3: the scanned garment's ORDER is on hold. Uses warning (amber) severity —
   * on hold is reversible/non-terminal (unlike cancelled, which is a red terminal block).
   * Shows the human-readable hold reason (reuses the issues.onHold.reason.* labels).
   */
  const triggerOrderOnHold = useCallback((holdReason?: string | null) => {
    const reasonKey = holdReason || 'manual';
    triggerWarning(
      t('scanFeedback.orderOnHold'),
      t('scanFeedback.orderOnHoldDesc', { reason: t(`issues.onHold.reason.${reasonKey}`) }),
      t('scanFeedback.orderOnHoldAction'),
    );
  }, [triggerWarning, t]);

  /**
   * The scanned garment's order was pulled for missing stock (INVENTORY_ERROR).
   * Amber, and always the translated copy: never the backend's English message,
   * so a zh floor reads it in Chinese. Shared by the catcher and print station.
   */
  const triggerStockIssue = useCallback(() => {
    triggerWarning(
      t('catcher.stockIssue'),
      t('catcher.stockIssueDesc'),
      t('catcher.stockIssueAction'),
    );
  }, [triggerWarning, t]);

  /**
   * Print station: a PAP product was scanned. PAP is pre-decorated (pick-and-
   * pack) and must never print — hard red block.
   */
  const triggerPapBlocked = useCallback(() => {
    triggerError(t('printer.papBlockedTitle'), t('printer.papBlockedBody'));
  }, [triggerError, t]);

  /**
   * Print station: garment is PENDING — the batch was made but its QR labels
   * were never printed. Amber, instructional; names the batch number when known.
   */
  const triggerPendingLabels = useCallback((batchNumber: number | null) => {
    const body = batchNumber != null
      ? t('printer.pendingLabelsBody', { batch: batchNumber })
      : t('printer.pendingLabelsBodyNoBatch');
    triggerWarning(t('printer.pendingLabelsTitle'), body, t('printer.pendingLabelsHint'));
  }, [triggerWarning, t]);

  /**
   * Convenience method for invalid QR code.
   */
  const triggerInvalidQR = useCallback(() => {
    triggerError(t('scanFeedback.invalidQr'), t('scanFeedback.invalidQrDesc'));
  }, [triggerError, t]);

  const dismissModal = useCallback(() => {
    setState(prev => ({ ...prev, showModal: false }));
  }, []);

  // Props object for spreading to ScanErrorModal
  // NOT memoized - object reference changes are fine, React handles this efficiently
  const modalProps = {
    open: state.showModal,
    onClose: dismissModal,
    title: state.title,
    message: state.message,
    secondaryMessage: state.secondaryMessage,
    severity: state.severity,
  };

  // Props object for spreading to SupersededStickerDialog. The station supplies
  // `locationId` + `currentStage` alongside these (not held in the hook).
  const supersededProps = {
    open: superseded.open,
    onClose: dismissSuperseded,
    orderNumber: superseded.orderNumber,
    replacement: superseded.replacement,
  };

  return {
    // Sound + modal triggers
    triggerSuccess,
    triggerError,
    triggerWarning,
    triggerSuccessModal,

    // Convenience methods
    triggerAlreadyScanned,
    triggerRedoBlocked,
    triggerSuperseded,
    triggerOrderCancelled,
    triggerOrderItemRemoved,
    triggerOrderChangedRemoved,
    triggerOrderOnHold,
    triggerStockIssue,
    triggerPapBlocked,
    triggerPendingLabels,
    triggerInvalidQR,

    // Modal control
    dismissModal,
    modalProps,
    dismissSuperseded,
    supersededProps,
    // True while EITHER the scan-error modal or the superseded dialog is open, so
    // stations pause scanner auto-focus for both (§6.1 one-blocking-modal-at-a-time).
    isModalOpen: state.showModal || superseded.open,
  };
}
